import React from 'react'
import { connect } from 'react-redux'
import { reportError } from '@app/store/slices/error-slice'

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: (resetError: () => void) => React.ReactNode
  section?: string
  reportError: typeof reportError
}

interface ErrorBoundaryState {
  hasError: boolean
}

/** Nieświeży chunk po deployu: sesja prosi o moduł, którego nie ma już na serwerze.
 * Jedynym ratunkiem jest przeładowanie strony, a znacznik w `sessionStorage` chroni
 * przed pętlą, gdy przyczyną błędu jest coś innego niż deploy.
 *
 * Funkcja jest współdzielona z granicą błędów tras (`app-router`): RouterProvider
 * łapie wyjątki renderu tras sam i nie przepuszcza ich do tej klasy, więc obie
 * granice muszą znać ten sam ratunek. Zwraca `true`, gdy przeładowanie ruszyło. */
export const recoverFromChunkError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : ''

  if (
    !message.includes('Failed to fetch dynamically imported module') &&
    !message.includes('Loading chunk') &&
    !message.includes('Loading CSS chunk')
  ) {
    return false
  }

  const reloadKey = 'chunk-error-reload'
  const lastReload = sessionStorage.getItem(reloadKey)
  const now = Date.now()

  if (lastReload && now - Number(lastReload) <= 10_000) return false

  sessionStorage.setItem(reloadKey, String(now))
  window.location.reload()

  return true
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Auto-reload on stale chunk errors after deployment
    if (recoverFromChunkError(error)) return

    const { section } = this.props
    if (section) {
      this.props.reportError({
        partKey: section,
        message: error.message,
        error: true,
      })
    } else {
      console.warn(`Section not specified.`)
    }

    const sectionInfo = section ? ` in section ${section}` : ''
    console.error(`Error caught${sectionInfo}: `, error, errorInfo)
  }

  resetError = () => {
    this.setState({ hasError: false })
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback?.(this.resetError) ?? null
    }
    return this.props.children
  }
}

const mapDispatchToProps = {
  reportError,
}

export default connect(null, mapDispatchToProps)(ErrorBoundary)
